import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { blogPosts } from '../data/mockData';
import { ArrowRight, Calendar, User, Search, Filter } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const ResourcesPage = () => {
  const categories = ['All', 'AI Insights', 'Performance', 'Strategy', 'Leadership'];
  const [activeCategory, setActiveCategory] = React.useState('All');

  const additionalPosts = [
    {
      id: 4,
      title: 'Breaking Through with Executives: How Performance Insights Builds Credibility',
      excerpt: 'Learn how data-driven insights help CXOs gain credibility with stakeholders and drive organizational change.',
      date: 'April 2, 2025',
      category: 'Strategy',
      author: 'Vikram Mehta',
      image: 'https://images.unsplash.com/photo-1553028826-f4804a6dba3b?w=400',
    },
    {
      id: 5,
      title: 'Organizational Performance Slowdown: Why Teams Are Struggling',
      excerpt: 'Analysis of common performance bottlenecks and how AI-driven insights can help identify solutions.',
      date: 'March 26, 2025',
      category: 'Performance',
      author: 'Priya Krishnamurthy',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400',
    },
    {
      id: 6,
      title: 'The Future of Executive Decision Making with AI',
      excerpt: 'How Agentic AI is transforming the way CXOs approach strategic planning and performance optimization.',
      date: 'March 15, 2025',
      category: 'AI Insights',
      author: 'Arjun Reddy',
      image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=400',
    },
  ];

  const allPosts = [...blogPosts, ...additionalPosts];

  return (
    <div className="min-h-screen">
      <Navbar />
      
      <main>
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
          <div className="absolute inset-0">
            <div className="absolute top-1/3 left-1/3 w-96 h-96 bg-emerald-500/10 rounded-full filter blur-3xl" />
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Resources & Insights
              </h1>
              <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                Explore our latest insights, tips, and resources on AI-powered performance management and executive leadership.
              </p>
            </div>
          </div>
        </section>

        {/* Search and Filter */}
        <section className="py-8 bg-white border-b sticky top-20 z-30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
              <div className="relative w-full md:w-96">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input 
                  placeholder="Search articles..." 
                  className="pl-10 w-full"
                />
              </div>
              <div className="flex gap-2 flex-wrap">
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => setActiveCategory(category)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      activeCategory === category
                        ? 'bg-emerald-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Featured Post */}
        <section className="py-12 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="grid md:grid-cols-2">
                <div className="h-64 md:h-auto">
                  <img
                    src={allPosts[0].image}
                    alt={allPosts[0].title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-8 md:p-12 flex flex-col justify-center">
                  <div className="flex items-center gap-4 mb-4">
                    <span className="px-3 py-1 bg-emerald-100 text-emerald-600 text-sm font-medium rounded-full">
                      Featured
                    </span>
                    <span className="text-sm text-gray-500">{allPosts[0].date}</span>
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                    {allPosts[0].title}
                  </h2>
                  <p className="text-gray-600 mb-6">{allPosts[0].excerpt}</p>
                  <Link
                    to={`/resources/${allPosts[0].id}`}
                    className="inline-flex items-center text-emerald-600 font-medium hover:text-emerald-700"
                  >
                    Read Article
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* All Posts Grid */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {allPosts.slice(1).map((post) => (
                <article
                  key={post.id}
                  className="group bg-white rounded-2xl border border-gray-200 overflow-hidden hover:shadow-xl transition-all duration-300"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 bg-emerald-600 text-white text-xs font-medium rounded-full">
                        {post.category}
                      </span>
                    </div>
                  </div>

                  <div className="p-6">
                    <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {post.date}
                      </span>
                      <span className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        {post.author}
                      </span>
                    </div>

                    <h3 className="text-lg font-bold text-gray-900 mb-3 group-hover:text-emerald-600 transition-colors line-clamp-2">
                      {post.title}
                    </h3>

                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                      {post.excerpt}
                    </p>

                    <Link
                      to={`/resources/${post.id}`}
                      className="inline-flex items-center text-emerald-600 font-medium text-sm hover:text-emerald-700"
                    >
                      Read More
                      <ArrowRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </article>
              ))}
            </div>

            {/* Load More */}
            <div className="text-center mt-12">
              <Button variant="outline" size="lg">
                Load More Articles
              </Button>
            </div>
          </div>
        </section>

        {/* Newsletter CTA */}
        <section className="py-20 bg-gray-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Stay Ahead with SAMANVAI Insights
            </h2>
            <p className="text-gray-400 mb-8">
              Subscribe to receive the latest articles, research, and performance insights directly in your inbox.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <Input 
                type="email" 
                placeholder="Enter your email" 
                className="flex-1 bg-gray-800 border-gray-700 text-white"
              />
              <Button className="bg-emerald-600 hover:bg-emerald-700">
                Subscribe
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ResourcesPage;
